import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Col, Form, Row, Table } from 'react-bootstrap';

const formatMNOK = (value: number) =>
  value.toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
import { IcebergButton } from '@tradesolution/iceberg-ui-react';

import PageWrapper from 'components/PageWrapper';
import CommonLoader from 'components/CommonLoader';
import DagligvareOmsetningApi from 'services/KjederegisteretAdminApi/DagligvareOmsetningApi';
import {
  OmsetningBatchEnhetDto,
  OmsetningBatchResponse,
  UpdateOmsetningBatchEnhetRequest,
} from 'services/KjederegisteretAdminApi/DagligvareOmsetningApi/types';
import useToaster from 'utils/hooks/useToaster';
import HttpErrorHelper from 'utils/HttpErrorHelper';

// ─── EnhetRow sub-component ──────────────────────────────────────────────────

interface EnhetRowProps {
  enhet: OmsetningBatchEnhetDto;
  onUpdate: (enhetId: number, req: UpdateOmsetningBatchEnhetRequest) => Promise<void>;
  isPublished: boolean;
}

const EnhetRow = ({ enhet, onUpdate, isPublished }: EnhetRowProps) => {
  const [proposedValue, setProposedValue] = useState<string>(
    enhet.proposedOmsetningNokMillions != null ? String(enhet.proposedOmsetningNokMillions) : '',
  );
  const [isSaving, setIsSaving] = useState(false);

  // Sync input when the batch updates this enhet from outside (e.g. after file-row click)
  useEffect(() => {
    setProposedValue(
      enhet.proposedOmsetningNokMillions != null ? String(enhet.proposedOmsetningNokMillions) : '',
    );
  }, [enhet.proposedOmsetningNokMillions]);

  const doUpdate = async (req: UpdateOmsetningBatchEnhetRequest) => {
    setIsSaving(true);
    try {
      await onUpdate(enhet.enhetId, req);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBlur = async () => {
    const num = parseFloat(proposedValue);
    if (!isNaN(num) && num !== enhet.proposedOmsetningNokMillions) {
      await doUpdate({ proposedOmsetningNokMillions: num });
    }
  };

  const handleReject = async () => {
    await doUpdate({ status: 'Rejected' });
  };

  const handleUnreject = async () => {
    await doUpdate({ status: 'Pending' });
  };

  const handleMarkHandled = async () => {
    await doUpdate({ status: 'Handled' });
  };

  const isNedlagt = enhet.nedlagtDato != null;
  const rowClass =
    enhet.status === 'Accepted'
      ? 'table-success'
      : enhet.status === 'Rejected'
        ? 'table-danger'
        : enhet.status === 'Handled'
          ? 'table-info'
          : '';

  const statusBadge =
    enhet.status === 'Rejected' ? (
      <Badge bg="danger">Avvist</Badge>
    ) : enhet.status === 'Handled' ? (
      <Badge bg="success">Behandlet</Badge>
    ) : null;

  return (
    <>
      <tr className={rowClass}>
        <td>
          {enhet.enhetNavn}
          {isNedlagt && (
            <Badge bg="warning" text="dark" className="ms-2" title={`Nedlagt ${enhet.nedlagtDato}`}>
              Nedlagt
            </Badge>
          )}
          {enhet.oppstartsDato != null && (
            <div style={{ fontSize: '0.8em' }} className="text-muted">
              {new Date(enhet.oppstartsDato).toLocaleDateString('nb-NO')}
              {' – '}
              {enhet.nedlagtDato ? new Date(enhet.nedlagtDato).toLocaleDateString('nb-NO') : ''}
            </div>
          )}
        </td>
        <td>{statusBadge}</td>
        <td>
          <Link to={`/enheter/${enhet.enhetId}`} target="_blank" rel="noopener noreferrer">
            {enhet.lopenummer}
          </Link>
        </td>
        <td>{enhet.statistikknr}</td>
        <td className="text-end">{formatMNOK(enhet.currentOmsetningNokMillions)}</td>
        <td>
          {!isPublished ? (
            <div className="d-flex align-items-center gap-1">
              <Form.Control
                type="number"
                size="sm"
                value={proposedValue}
                onChange={(e) => setProposedValue(e.target.value)}
                onBlur={handleBlur}
                style={{ width: '110px' }}
                disabled={isSaving || enhet.status === 'Rejected'}
                step="0.01"
              />
              {enhet.proposedOmsetningNokMillionsCalculatedForWholeYear != null && (
                <span className="text-muted">
                  ({formatMNOK(enhet.proposedOmsetningNokMillionsCalculatedForWholeYear)})
                </span>
              )}
            </div>
          ) : (
            <span>
              {enhet.proposedOmsetningNokMillions != null
                ? formatMNOK(enhet.proposedOmsetningNokMillions)
                : '—'}
              {enhet.proposedOmsetningNokMillionsCalculatedForWholeYear != null && (
                <span className="text-muted ms-1">
                  ({formatMNOK(enhet.proposedOmsetningNokMillionsCalculatedForWholeYear)})
                </span>
              )}
            </span>
          )}
        </td>
        <td className="text-end">
          {enhet.proposedOmsetningNokMillions != null && enhet.currentOmsetningNokMillions !== 0 ? (
            (() => {
              const diff = enhet.proposedOmsetningNokMillions - enhet.currentOmsetningNokMillions;
              const pct = (diff / enhet.currentOmsetningNokMillions) * 100;
              const color = pct > 0 ? 'text-success' : pct < 0 ? 'text-danger' : 'text-muted';
              return (
                <span className={color}>
                  {pct > 0 ? '+' : ''}
                  {pct.toLocaleString('nb-NO', {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}
                  %
                  <span className="text-muted ms-1" style={{ fontSize: '0.85em' }}>
                    ({diff > 0 ? '+' : ''}
                    {formatMNOK(diff)})
                  </span>
                </span>
              );
            })()
          ) : (
            <span className="text-muted">—</span>
          )}
        </td>
        <td>
          {!isPublished && (
            <div className="d-flex gap-1">
              {enhet.status !== 'Rejected' ? (
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={handleReject}
                  disabled={isSaving}
                  title="Avvis"
                >
                  ✗
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={handleUnreject}
                  disabled={isSaving}
                >
                  Angre
                </Button>
              )}
              {enhet.status === 'Handled' ? (
                <Button
                  size="sm"
                  variant="success"
                  onClick={handleUnreject}
                  disabled={isSaving}
                  title="Angre behandlet"
                >
                  Behandlet
                </Button>
              ) : enhet.status !== 'Rejected' ? (
                <Button
                  size="sm"
                  variant="outline-success"
                  onClick={handleMarkHandled}
                  disabled={isSaving}
                  title="Merk som behandlet"
                >
                  Behandlet
                </Button>
              ) : null}
            </div>
          )}
        </td>
      </tr>
      {enhet.rows.map((row, i) => (
        <tr key={i} style={{ fontSize: '0.85em' }} className="table-light">
          <td colSpan={4} className="ps-4 text-muted">
            📄 <strong>{row.fileName}</strong>
            {row.butikknavn && ` · ${row.butikknavn}`}
            {row.gln && ` · GLN: ${row.gln}`}
            {row.gateadresse &&
              ` · ${row.gateadresse}${row.postnummer ? ` ${row.postnummer}` : ''}`}
            {row.oppstartsDato != null && (
              <div style={{ fontSize: '0.8em' }} className="text-muted">
                {new Date(row.oppstartsDato).toLocaleDateString('nb-NO')}
                {' – '}
                {row.nedlagtDato ? new Date(row.nedlagtDato).toLocaleDateString('nb-NO') : ''}
              </div>
            )}
          </td>
          <td className="text-end text-muted">—</td>
          <td className="text-end text-body">
            {row.omsetningNokMillions != null ? (
              <span>
                {formatMNOK(row.omsetningNokMillions)} <span className="text-muted">(MNOK)</span>
              </span>
            ) : (
              <span className="text-muted">—</span>
            )}
          </td>
          <td />
          <td />
        </tr>
      ))}
    </>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

const DagligvareOmsetningImport = () => {
  const toaster = useToaster();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [batch, setBatch] = useState<OmsetningBatchResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishConfirming, setPublishConfirming] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [showRejected, setShowRejected] = useState(false);
  const [showMissingOnly, setShowMissingOnly] = useState(false);
  const [sortByDiff, setSortByDiff] = useState<'none' | 'desc' | 'asc'>('none');

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const b = await DagligvareOmsetningApi.getBatch();
        setBatch(b);
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          toaster.error('Feil ved lasting', HttpErrorHelper.formatError(err));
        }
        setBatch(null);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setIsUploading(true);
    try {
      const result = await DagligvareOmsetningApi.importBatch(file);
      setBatch(result);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      toaster.success('Fil importert', `${result.enheter.length} enheter lastet inn`);
    } catch (err) {
      toaster.error('Import feilet', HttpErrorHelper.formatError(err));
    } finally {
      setIsUploading(false);
    }
  };

  const handleUpdateEnhet = async (enhetId: number, req: UpdateOmsetningBatchEnhetRequest) => {
    try {
      const updated = await DagligvareOmsetningApi.updateEnhet(enhetId, req);
      setBatch((prev) =>
        prev
          ? {
              ...prev,
              enheter: prev.enheter.map((e) => (e.enhetId === updated.enhetId ? updated : e)),
            }
          : prev,
      );
    } catch (err) {
      toaster.error('Lagring feilet', HttpErrorHelper.formatError(err));
      throw err;
    }
  };

  const handlePublish = async () => {
    if (!batch) return;
    setPublishConfirming(false);
    setIsPublishing(true);
    try {
      const result = await DagligvareOmsetningApi.publishBatch();
      setBatch(result);
      toaster.success('Publisert', 'Omsetning er lagret i databasen');
    } catch (err) {
      toaster.error('Publisering feilet', HttpErrorHelper.formatError(err));
    } finally {
      setIsPublishing(false);
    }
  };

  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteBatch = async () => {
    if (!window.confirm('Er du sikker på at du vil slette hele batchen? Dette kan ikke angres.'))
      return;
    setIsDeleting(true);
    try {
      await DagligvareOmsetningApi.deleteBatch();
      setBatch(null);
      toaster.success('Slettet', 'Batchen er slettet');
    } catch (err) {
      toaster.error('Sletting feilet', HttpErrorHelper.formatError(err));
    } finally {
      setIsDeleting(false);
    }
  };

  // Counters
  const total = batch?.enheter.length ?? 0;
  const accepted = batch?.enheter.filter((e) => e.status === 'Accepted').length ?? 0;
  const rejected = batch?.enheter.filter((e) => e.status === 'Rejected').length ?? 0;
  const handled = batch?.enheter.filter((e) => e.status === 'Handled').length ?? 0;
  const missingValue =
    batch?.enheter.filter((e) => e.status !== 'Rejected' && e.proposedOmsetningNokMillions == null)
      .length ?? 0;
  const totalProposedMNOK =
    batch?.enheter
      .filter((e) => e.status !== 'Rejected' && e.proposedOmsetningNokMillions != null)
      .reduce((sum, e) => sum + e.proposedOmsetningNokMillions!, 0) ?? 0;

  const filteredEnheter =
    batch?.enheter.filter(
      (e) =>
        (showRejected || e.status !== 'Rejected') &&
        (!showMissingOnly || e.proposedOmsetningNokMillions == null),
    ) ?? [];

  const getDiff = (e: (typeof filteredEnheter)[0]) =>
    e.proposedOmsetningNokMillions != null && e.currentOmsetningNokMillions !== 0
      ? ((e.proposedOmsetningNokMillions - e.currentOmsetningNokMillions) /
          e.currentOmsetningNokMillions) *
        100
      : null;

  const visibleEnheter = [...filteredEnheter].sort((a, b) => {
    if (sortByDiff !== 'none') {
      const aDiff = getDiff(a);
      const bDiff = getDiff(b);
      if (aDiff == null && bDiff == null) return 0;
      if (aDiff == null) return 1;
      if (bDiff == null) return -1;
      return sortByDiff === 'desc' ? bDiff - aDiff : aDiff - bDiff;
    }
    const rowDiff = b.rows.length - a.rows.length;
    if (rowDiff !== 0) return rowDiff;
    const aDate = a.oppstartsDato ? new Date(a.oppstartsDato).getTime() : -Infinity;
    const bDate = b.oppstartsDato ? new Date(b.oppstartsDato).getTime() : -Infinity;
    return bDate - aDate;
  });

  const handleDiffHeaderClick = () =>
    setSortByDiff((prev) => (prev === 'none' ? 'desc' : prev === 'desc' ? 'asc' : 'none'));

  const diffSortIndicator = sortByDiff === 'desc' ? ' ▼' : sortByDiff === 'asc' ? ' ▲' : ' ⇅';

  const isPublished = batch?.status === 'Published';

  return (
    <PageWrapper header="Dagligvare omsetning - Import">
      {isLoading ? (
        <CommonLoader />
      ) : (
        <>
          {/* Upload section — hidden after publish */}
          {!isPublished && (
            <Row className="mb-3">
              <Col md={5}>
                <Form.Group>
                  <Form.Label>Last opp Excel-fil</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".xlsx,.xls"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    disabled={isUploading}
                  />
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <IcebergButton
                  icon="import"
                  variant="primary"
                  onClick={handleUpload}
                  disabled={!file || isUploading}
                >
                  {isUploading ? 'Laster opp...' : 'Last opp fil'}
                </IcebergButton>
              </Col>
            </Row>
          )}

          {/* Empty state */}
          {!batch && (
            <Alert variant="info">Ingen aktiv batch. Last opp en Excel-fil for å starte.</Alert>
          )}

          {batch && (
            <>
              {/* Published banner */}
              {isPublished && (
                <Alert variant="success" className="mb-3">
                  Batch publisert{' '}
                  {batch.publishedAt ? new Date(batch.publishedAt).toLocaleString('nb-NO') : ''} —
                  vises skrivebeskyttet.
                </Alert>
              )}

              {/* Imported files */}
              {batch.files.length > 0 && (
                <div className="mb-3">
                  <h6>Importerte filer ({batch.year})</h6>
                  <Table size="sm" bordered style={{ maxWidth: '720px' }}>
                    <thead>
                      <tr>
                        <th>Filnavn</th>
                        <th>Importert</th>
                        <th className="text-end">Rader</th>
                        <th className="text-end">Total omsetning (MNOK)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batch.files.map((f) => (
                        <tr key={f.fileName}>
                          <td>{f.fileName}</td>
                          <td>{new Date(f.importedAt).toLocaleString('nb-NO')}</td>
                          <td className="text-end">{f.rowCount}</td>
                          <td className="text-end">
                            {f.totalOmsetning != null ? formatMNOK(f.totalOmsetning) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {batch.files.some((f) => f.totalOmsetning != null) && (
                      <tfoot>
                        <tr className="fw-semibold">
                          <td colSpan={2}>Totalt</td>
                          <td className="text-end">
                            {batch.files.reduce((sum, f) => sum + f.rowCount, 0)}
                          </td>
                          <td className="text-end">
                            {formatMNOK(
                              batch.files.reduce((sum, f) => sum + (f.totalOmsetning ?? 0), 0),
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </Table>
                </div>
              )}

              {/* Counters */}
              <div className="mb-3 d-flex flex-wrap gap-3 align-items-center">
                <span>
                  Totalt: <Badge bg="secondary">{total}</Badge>
                </span>
                <span>
                  Avvist: <Badge bg="danger">{rejected}</Badge>
                </span>
                {handled > 0 && (
                  <span>
                    Behandlet: <Badge bg="success">{handled}</Badge>
                  </span>
                )}
                <span>
                  Total foreslått:{' '}
                  <Badge bg="secondary">{formatMNOK(totalProposedMNOK)} MNOK</Badge>
                </span>
                {missingValue > 0 && (
                  <span>
                    Mangler verdi:{' '}
                    <Badge bg="warning" text="dark">
                      {missingValue}
                    </Badge>
                  </span>
                )}
                {rejected > 0 && (
                  <Form.Check
                    type="switch"
                    id="show-rejected"
                    label="Vis avviste"
                    checked={showRejected}
                    onChange={(e) => setShowRejected(e.target.checked)}
                  />
                )}
                {missingValue > 0 && (
                  <Form.Check
                    type="switch"
                    id="show-missing-only"
                    label="Vis kun uten foreslått"
                    checked={showMissingOnly}
                    onChange={(e) => setShowMissingOnly(e.target.checked)}
                  />
                )}
                {!isPublished && (
                  <div className="ms-auto d-flex gap-2 align-items-center">
                    {publishConfirming ? (
                      <>
                        <span className="text-muted" style={{ fontSize: '0.9em' }}>
                          {missingValue > 0
                            ? `${missingValue} enheter mangler verdi og hoppes over. Fortsette?`
                            : 'Er du sikker?'}
                        </span>
                        <IcebergButton
                          icon="disk"
                          variant="primary"
                          onClick={handlePublish}
                          disabled={isPublishing}
                        >
                          {isPublishing ? 'Publiserer...' : 'Bekreft'}
                        </IcebergButton>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setPublishConfirming(false)}
                        >
                          Avbryt
                        </Button>
                      </>
                    ) : (
                      <IcebergButton
                        icon="disk"
                        variant="primary"
                        onClick={() => setPublishConfirming(true)}
                        disabled={total === 0 || isPublishing}
                      >
                        Publiser
                      </IcebergButton>
                    )}
                    <Button
                      variant="outline-danger"
                      onClick={handleDeleteBatch}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Sletter...' : 'Slett batch'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Enheter table */}
              <Table hover size="sm" responsive>
                <thead>
                  <tr>
                    <th>Enhet</th>
                    <th>Status</th>
                    <th>Løpenr</th>
                    <th>Statistikknr</th>
                    <th className="text-end">Nå (MNOK)</th>
                    <th>Foreslått (MNOK)</th>
                    <th
                      className="text-end"
                      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                      onClick={handleDiffHeaderClick}
                      title="Sorter etter diff"
                    >
                      Diff (%){diffSortIndicator}
                    </th>
                    <th>Handling</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleEnheter.map((enhet) => (
                    <EnhetRow
                      key={enhet.enhetId}
                      enhet={enhet}
                      onUpdate={handleUpdateEnhet}
                      isPublished={!!isPublished}
                    />
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </>
      )}
    </PageWrapper>
  );
};

export default DagligvareOmsetningImport;
